import { useQuery } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { fetchStorageDownloadUrl } from "@apps/dashboard/lib/home-expense-api";
import {
  isDirectExternalAvatarUrl,
  storageObjectKeyFromUserImageField,
} from "@apps/dashboard/lib/storage-user-image";

const SIGNED_URL_TTL_SEC = 60 * 60 * 24; // 24 h; debe ser < caducidad máxima del endpoint

/**
 * Resuelve la URL a usar en `<img>` para `user.image`: claves `uploads/*` → GET firmado;
 * URLs externas sin clave → la misma cadena.
 */
export function useUserAvatarDisplayUrl(raw: string | null | undefined): {
  displayUrl: string | null;
  isLoading: boolean;
  isError: boolean;
} {
  const { locale } = useI18n();
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  const key = trimmed ? storageObjectKeyFromUserImageField(trimmed) : null;
  const useDirect = Boolean(trimmed && !key && isDirectExternalAvatarUrl(trimmed));

  const signedQuery = useQuery({
    queryKey: ["storage-user-avatar-url", locale, key],
    queryFn: async () => {
      if (!key) throw new Error("missing key");
      const { url } = await fetchStorageDownloadUrl(locale, key, SIGNED_URL_TTL_SEC);
      return url;
    },
    enabled: Boolean(key),
    staleTime: (SIGNED_URL_TTL_SEC - 15 * 60) * 1000,
  });

  if (!trimmed) {
    return { displayUrl: null, isLoading: false, isError: false };
  }
  if (key) {
    return {
      displayUrl: signedQuery.data ?? null,
      isLoading: signedQuery.isLoading,
      isError: signedQuery.isError,
    };
  }
  if (useDirect) {
    return { displayUrl: trimmed, isLoading: false, isError: false };
  }
  return { displayUrl: null, isLoading: false, isError: false };
}
